import React, { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";

// Handle redirected paths from 404.html
(function() {
  // Check if we were redirected from 404.html
  const redirectPath = localStorage.getItem('redirectPath');
  if (redirectPath) {
    // Clear the stored path
    localStorage.removeItem('redirectPath');
    
    // Check if we're not already at the correct path
    if (window.location.pathname === '/' && redirectPath !== '/') {
      // Redirect to the stored path
      window.history.replaceState(null, '', redirectPath);
    }
  }
})();

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from "./lib/auth";
import { RouterProvider } from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { debugInitialization } from "./utils/debugHelper";
import { router } from "./lib/router";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from 'sonner';
import { Loading } from "./components/ui/loading";
import { LoadingProvider } from "./contexts/LoadingContext";
import { applyAllSafariFixes } from "./utils/safariPolyfills";
import { onCLS, onFID, onFCP, onLCP, onTTFB } from 'web-vitals';
import './utils/routerWarnings';

// Import App component
import App from "./App";

// Import CookieAuthInitializer
import { CookieAuthInitializer } from "./components/auth/CookieAuthInitializer";

// Import essential providers that are actually used by components
import { CsrfProvider } from "./contexts/CsrfContext";
import { CartProvider } from "./contexts/CartContext";
import { ProductCareProvider } from "./contexts/ProductCareContext";
import { FAQProvider } from "./contexts/FAQContext";
import { BespokeProvider } from "./contexts/BespokeContext";
import { StorybookProvider } from "./contexts/StorybookContext";
import { RepairsProvider } from "./contexts/RepairsContext";
import { GiftingProvider } from "./contexts/GiftingContext";
import { TeluaseghStoryProvider } from './contexts/TeluaseghStoryContext';
import { ReturnsExchangeProvider } from './contexts/ReturnsExchangeContext';
import { PrivacyProvider } from './contexts/PrivacyContext';
import { ShippingProvider } from './contexts/ShippingContext';
import { TermsProvider } from './contexts/TermsContext';

// Safari-optimized React Query configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, // Reduced for Safari performance
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes (increased cache time)
      refetchOnWindowFocus: false,
      refetchOnReconnect: false, // Reduced network activity for Safari
    },
    mutations: {
      retry: 1
    }
  }
});

// Safari-optimized provider wrapper - includes all providers needed by Footer
const OptimizedProviders = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CookieAuthInitializer />
            <LoadingProvider>
              <CsrfProvider>
                <CartProvider>
                  <ProductCareProvider>
                    <FAQProvider>
                      <BespokeProvider>
                        <StorybookProvider>
                          <RepairsProvider>
                            <GiftingProvider>
                              <TeluaseghStoryProvider>
                                <ReturnsExchangeProvider>
                                  <PrivacyProvider>
                                    <ShippingProvider>
                                      <TermsProvider>
                                        {children}
                                      </TermsProvider>
                                    </ShippingProvider>
                                  </PrivacyProvider>
                                </ReturnsExchangeProvider>
                              </TeluaseghStoryProvider>
                            </GiftingProvider>
                          </RepairsProvider>
                        </StorybookProvider>
                      </BespokeProvider>
                    </FAQProvider>
                  </ProductCareProvider>
                </CartProvider>
              </CsrfProvider>
            </LoadingProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

// Lightweight Safari performance monitoring
const reportWebVitals = (onPerfEntry?: (metric: { id: string; name: string; value: number }) => void) => {
  if (onPerfEntry && typeof onPerfEntry === 'function') {
    onCLS(onPerfEntry);
    onFID(onPerfEntry);
    onFCP(onPerfEntry);
    onLCP(onPerfEntry);
    onTTFB(onPerfEntry);
  }
};

// Safari-optimized app wrapper
function SafariOptimizedApp() {
  useEffect(() => {
    // Minimal Safari-specific optimizations
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    if (isSafari) {
      // Critical Safari fixes only
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
      
      // Prevent Safari zoom on input focus
      const style = document.createElement('style');
      style.textContent = `
        input, textarea, select {
          font-size: 16px !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <OptimizedProviders>
      <Suspense fallback={<Loading />}>
        <App />
        <Toaster />
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </Suspense>
    </OptimizedProviders>
  );
}

// Initialize performance monitoring
if (typeof window !== 'undefined') {
  reportWebVitals();
}

// Store root reference to prevent multiple createRoot calls
let root: ReactDOM.Root | null = null;

function renderApp() {
  const container = document.getElementById("root");
  if (!container) {
    throw new Error("Root element not found");
  }

  if (!root) {
    root = ReactDOM.createRoot(container);
  }
  
  root.render(<SafariOptimizedApp />);
}

renderApp();
